import React, { useCallback, useEffect, useState, useRef } from 'react';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { useFetchAggregatedShipToPartiesForTxa } from '@css-xp/ppc-api';
import { DEFAULT_PAGE_SIZE } from '@css-xp/ppc-constants';
import {
  AggregatedShipToPartyOptions,
  CommerceAddress,
  PartnerSearchOptions,
  PartnerSearchContext,
} from '@css-xp/ppc-graphtypes';
import { CUSTOMERS_PAGE_UTILS } from '@css-xp/ppc-utils';
import { emptyStateProp } from '@css-xp/ppc-common';
import { SearchSoldToAddressTableV2 } from './search-sold-to-address-table-v2';

interface AggregatedSoldToAddressProps {
  txaId: string;
  billToAddress?: CommerceAddress | null;
  hidePricingEligibility?: boolean;
  showAddAddress?: boolean;
  hideActions?: boolean;
  showSearchBox?: boolean;
  searchTerm?: string;
  hideEdit?: boolean;
  hideAddFromBillToAddress?: boolean;
  partnerSearchContext?: string;
  showAddAddressInRow?: boolean;
  emptyState?: any;
  heading?: React.ReactNode;
  updateCsvSearchOptions?: (value: any) => void;
  setIsExportCsvButtonDisabled?: (value: boolean) => void;
}

export const AggregatedSoldToAddressV2 = ({
  txaId,
  billToAddress,
  hidePricingEligibility,
  showAddAddress,
  hideActions,
  showSearchBox,
  searchTerm = '',
  hideEdit,
  hideAddFromBillToAddress,
  partnerSearchContext,
  showAddAddressInRow = true,
  emptyState = emptyStateProp,
  heading,
  updateCsvSearchOptions,
  setIsExportCsvButtonDisabled,
}: AggregatedSoldToAddressProps) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [tableLevelLoading, setTableLevelLoading] = useState<boolean>(true);
  const [tableRows, setTableRows] = useState<Array<unknown>>([]);
  const [previousSearchContext, setPreviousSearchContext] = useState<PartnerSearchContext | undefined>(undefined);
  const aggregatedOptionsRef = useRef<AggregatedShipToPartyOptions>(
    CUSTOMERS_PAGE_UTILS.buildAggregatedOptions(searchTerm, partnerSearchContext)
  );
  useEffect(() => {
    aggregatedOptionsRef.current = CUSTOMERS_PAGE_UTILS.buildAggregatedOptions(searchTerm, partnerSearchContext);
  }, [partnerSearchContext]);

  const { data, loading, refetch: refreshAggregatedShipToParties, error } = useFetchAggregatedShipToPartiesForTxa({
    txaId,
    aggregatedShipToPartyOptions: aggregatedOptionsRef.current,
  });
  const refetch = useCallback(
    (variables) => {
      updateCsvSearchOptions?.(variables?.aggregatedShipToPartyOptions?.searchOptions);
      return refreshAggregatedShipToParties(variables);
    },
    [refreshAggregatedShipToParties]
  );
  // Once the search context is changed, need to remove load more button and enable table level loading
  useEffect(() => {
    if (loading) {
      setTableRows([]);
    }
    setHasNextPage(false);
    setTableLevelLoading(true);
  }, [partnerSearchContext]);

  useEffect(() => {
    if (!loading) {
      const nodes = data?.transactionAccount?.aggregatedShipToParty?.nodes;
      if (partnerSearchContext === previousSearchContext) {
        // This is for load more on same search context.
        //@ts-ignore
        setTableRows([...tableRows, ...nodes]);
      } else {
        //@ts-ignore
        setTableRows([...nodes]);
      }
      setTableLevelLoading(true);
      setHasNextPage(data?.transactionAccount.aggregatedShipToParty?.pageInfo?.hasNextPage ?? false);
      setEndCursor(data?.transactionAccount.aggregatedShipToParty?.pageInfo?.endCursor ?? undefined);
      //@ts-ignore
      setPreviousSearchContext(partnerSearchContext);
    }
  }, [data]);
  const onPageChange = useCallback(
    (newPage: number) => {
      const offset = (newPage - 1) * rowsPerPage;

      const newAggregatedShipToPartyOptions = {
        ...aggregatedOptionsRef.current,
        pageOptions: {
          cursor: String(offset),
          size: rowsPerPage,
        },
      };
      aggregatedOptionsRef.current = newAggregatedShipToPartyOptions;
      setCurrentPage(newPage);
      refetch({
        aggregatedShipToPartyOptions: newAggregatedShipToPartyOptions,
      });
    },
    [rowsPerPage, currentPage, data]
  );

  const handleRowChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) {
        return;
      }
      const newAggregatedShipToPartyOptions = {
        ...aggregatedOptionsRef.current,
        pageOptions: {
          cursor: null,
          size: newRowPerPage,
        },
      };
      aggregatedOptionsRef.current = newAggregatedShipToPartyOptions;
      setRowsPerPage(newRowPerPage);
      setCurrentPage(1);
      refetch({
        aggregatedShipToPartyOptions: newAggregatedShipToPartyOptions,
      });
    },
    [rowsPerPage, currentPage]
  );

  const onSearch = useCallback(
    (searchTerm: string) => {
      const newAggregatedShipToPartyOptions = {
        ...aggregatedOptionsRef.current,
        searchOptions: {
          query: searchTerm,
          searchContext: PartnerSearchContext.ALL,
        },
        pageOptions: {
          cursor: null,
          size: rowsPerPage,
        },
      };
      aggregatedOptionsRef.current = newAggregatedShipToPartyOptions;
      setCurrentPage(DEFAULT_PAGE);
      refetch({
        aggregatedShipToPartyOptions: newAggregatedShipToPartyOptions,
      });
    },
    [rowsPerPage]
  );
  const onLoadMore = useCallback(() => {
    if (endCursor) {
      setTableLevelLoading(false);
      const newAggregatedShipToPartyOptions = {
        ...aggregatedOptionsRef.current,
        searchOptions: {
          query: searchTerm,
          searchContext: PartnerSearchContext.ALL,
        },
        pageOptions: {
          cursor: endCursor,
          size: rowsPerPage,
        },
      };
      setHasNextPage(false);
      refetch({
        aggregatedShipToPartyOptions: newAggregatedShipToPartyOptions,
      });
    }
  }, [refetch, endCursor]);
  const response = data?.transactionAccount.aggregatedShipToParty;
  const totalCount = data?.transactionAccount.aggregatedShipToParty?.totalCount;
  setIsExportCsvButtonDisabled?.(!response?.totalCount);

  return (
    <div data-testid="transaction-account-ship-to-address">
      <SearchSoldToAddressTableV2
        heading={heading}
        error={error}
        searchTerm={searchTerm}
        addressResponse={tableRows}
        totalCount={totalCount}
        tableLevelLoading={tableLevelLoading}
        tableLoading={loading}
        transactionAccountId={txaId}
        billToAddress={billToAddress}
        onSearch={onSearch}
        hidePricingEligibility={hidePricingEligibility}
        showAddAddress={showAddAddress}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onRowChange={handleRowChange}
        onPageChange={onPageChange}
        hideActions={hideActions}
        showLoadMore={hasNextPage}
        onLoadMore={onLoadMore}
        showSearchBox={showSearchBox}
        tableRefetch={refetch}
        hideEdit={hideEdit}
        hideAddFromBillToAddress={hideAddFromBillToAddress}
        showAddAddressInRow={showAddAddressInRow}
        searchOptions={aggregatedOptionsRef.current.searchOptions as PartnerSearchOptions}
        emptyState={emptyState}
      />
    </div>
  );
};
